import type { ComponentChildren } from 'preact'
import type { ContactDetailDto } from '@/api/contactDetail'
import type { ContactTabKey } from '@/hooks/useContactTabRouter'
import { SignalBars } from '@/components/common/SignalBars'
import { ActivityRail } from '../ActivityRail'
import { WebSourceEditor, type WebSourceEditContext } from '../WebSourceEditor'
import { relativeTime } from '../contactFormat'

interface Props {
  detail: ContactDetailDto
  onGoTab: (tab: ContactTabKey) => void
  /** Whether the full Activity tab exists at this scope (campaign-scoped only). When false (workspace
   *  scope, view-only) the "View full activity" drill is hidden — it would route to a tab that isn't
   *  rendered there and leave the body blank. Required so a caller can't silently drop the drill by
   *  forgetting to pass it. */
  canViewActivity: boolean
  /** Present only in web scope with edit rights (ORB-608) — absent leaves the Web source rows read-only. */
  editWebSource?: WebSourceEditContext
}

export function ContactOverviewTab({ detail, onGoTab, canViewActivity, editWebSource }: Props) {
  const m = detail.Measures
  const hasActivity = detail.RecentActivity.length > 0
  const isCampaignScope = detail.Scope === 'Campaign' || detail.Scope === 'Company'

  return (
    <div>
      {/* signal strip — a typographic measure row, not cards */}
      <div class="flex flex-wrap items-center gap-x-7 gap-y-3 px-6 py-4 border-b border-slate-100 dark:border-slate-800/60">
        {!detail.IsAudienceOnly && detail.SignalScore != null && (
          <>
            <div class="flex items-baseline gap-1.5">
              <span class="text-2xl font-semibold tabular-nums text-slate-900 dark:text-white">{detail.SignalScore}</span>
              <span class="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">score</span>
            </div>
            <Divider />
          </>
        )}

        {m.EmailsSent > 0 ? (
          <>
            <div class="flex items-baseline gap-x-4">
              <Measure value={m.EmailsSent} label="emails" />
              {m.OpenRatePercent != null && <Measure value={`${m.OpenRatePercent}%`} label="open" />}
              <Measure value={m.Clicks} label="clicks" />
            </div>
            <Divider />
          </>
        ) : null}

        {(m.Sessions > 0 || m.Conversions > 0) ? (
          <>
            <div class="flex items-baseline gap-x-4">
              <Measure value={m.Sessions} label="sessions" />
              {m.Conversions > 0 && <Measure value={m.Conversions} label="conv" />}
            </div>
            <Divider />
          </>
        ) : null}

        {m.LastSeenUtc ? (
          <div class="flex items-baseline gap-1.5">
            <span class="text-sm font-medium text-slate-700 dark:text-slate-200">{relativeTime(m.LastSeenUtc)}</span>
            <span class="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">last seen</span>
          </div>
        ) : null}

        {m.DealStage && (
          <div class="flex items-baseline gap-1.5">
            <span class="text-sm font-medium text-slate-700 dark:text-slate-200">{m.DealStage}</span>
            <span class="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">deal</span>
          </div>
        )}

        {detail.IsAudienceOnly && m.EmailsSent === 0 && m.Sessions === 0 && (
          <div class="flex items-baseline gap-1.5 text-slate-400 dark:text-slate-500">
            <span class="text-sm">No emails, sessions, or score yet</span>
          </div>
        )}
      </div>

      {/* two-column body */}
      <div class="grid grid-cols-1 gap-6 px-6 py-6 lg:grid-cols-[1.55fr_1fr]">
        {/* LEFT: recent activity preview */}
        <div>
          <div class="mb-3 flex items-center justify-between">
            <h2 class="text-sm font-semibold text-slate-900 dark:text-white">Recent activity</h2>
            {hasActivity && canViewActivity && (
              <button type="button" onClick={() => onGoTab('activity')} class="text-xs font-medium text-primary-700 dark:text-primary-400 hover:underline">View full activity →</button>
            )}
          </div>
          {hasActivity ? (
            <ActivityRail items={detail.RecentActivity} showCampaignTag={isCampaignScope} />
          ) : (
            <div class="rounded-lg border border-dashed border-slate-200 dark:border-slate-700 px-5 py-8 text-center">
              <p class="text-sm font-medium text-slate-700 dark:text-slate-200">No activity yet</p>
              <p class="mx-auto mt-1 max-w-sm text-sm text-slate-500 dark:text-slate-400">
                {detail.TargetingListName ? <>Targeted via <span class="font-medium text-slate-600 dark:text-slate-300">{detail.TargetingListName}</span>. </> : null}
                Email engagement and web sessions appear here once they happen.
              </p>
            </div>
          )}
        </div>

        {/* RIGHT: details rail */}
        <aside class="space-y-5 lg:border-l lg:border-slate-100 lg:dark:border-slate-800/60 lg:pl-6">
          {detail.TargetingListName && (
            <Section title="Targeting">
              <Row label="List" value={detail.TargetingListName} strong />
              {detail.TargetingReason && <Row label="Reason" value={detail.TargetingReason} />}
            </Section>
          )}

          <Section title="Enrichment">
            <p class="text-sm text-slate-600 dark:text-slate-300">
              {detail.Enrichment.Status === 'Pending'
                ? 'Enriching…'
                : detail.Enrichment.EnrichedAt
                  ? `Enriched ${relativeTime(detail.Enrichment.EnrichedAt)}`
                  : 'Not enriched'}
            </p>
          </Section>

          {(detail.CompanyName || detail.CompanyIndustry || detail.CompanyStaffCountRange) && (
            <Section title="Company">
              {detail.CompanyName && <Row label="Name" value={detail.CompanyName} strong />}
              {detail.CompanyIndustry && <Row label="Industry" value={detail.CompanyIndustry} />}
              {detail.CompanyStaffCountRange && <Row label="Size" value={detail.CompanyStaffCountRange} />}
            </Section>
          )}

          {detail.WebSource && (
            <Section title="Web source">
              {editWebSource ? (
                <WebSourceEditor detail={detail} {...editWebSource} />
              ) : (
                <>
                  {detail.WebSource.Source && <Row label="Source" value={detail.WebSource.Source} />}
                  <Row label="Offer accepted" value={detail.WebSource.HasAcceptedOffer ? 'Yes' : 'No'} />
                </>
              )}
            </Section>
          )}

          {detail.CampaignTouchpoints.length > 0 && (
            <Section title={`Campaign touchpoints · ${detail.CampaignTouchpoints.length}`}>
              <ul class="space-y-2">
                {detail.CampaignTouchpoints.map(tp => (
                  <li key={tp.CampaignId} class="flex items-center justify-between gap-3">
                    <span class="min-w-0 truncate text-sm text-slate-700 dark:text-slate-300">{tp.CampaignName}</span>
                    <SignalBars score={tp.SignalScore} class="flex shrink-0 items-end h-4 w-8 gap-0.5" />
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {detail.Memberships.length > 0 && (
            <Section title="Memberships">
              <ul class="space-y-1.5 text-sm">
                {detail.Memberships.map((mem, i) => (
                  <li key={i} class="flex items-center gap-2">
                    <span class={`h-1.5 w-1.5 rounded-full ${mem.Kind === 'Lead' ? 'bg-purple-500' : 'bg-slate-400'}`} />
                    <span class="text-slate-700 dark:text-slate-300">{mem.Label}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </aside>
      </div>
    </div>
  )
}

function Divider() {
  return <div class="h-9 w-px bg-slate-200 dark:bg-slate-700/70" aria-hidden="true" />
}

function Measure({ value, label }: { value: string | number; label: string }) {
  return (
    <span>
      <span class="text-2xl font-semibold tabular-nums text-slate-900 dark:text-white">{value}</span>{' '}
      <span class="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</span>
    </span>
  )
}

function Section({ title, children }: { title: string; children: ComponentChildren }) {
  return (
    <div>
      <h3 class="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">{title}</h3>
      {children}
    </div>
  )
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <dl class="flex justify-between gap-3 text-sm">
      <dt class="text-slate-500 dark:text-slate-400">{label}</dt>
      <dd class={`text-right ${strong ? 'font-medium text-slate-800 dark:text-slate-200' : 'text-slate-700 dark:text-slate-300'}`}>{value}</dd>
    </dl>
  )
}
